import demoHints from "./demo_hints.json";
import type { DemoHintStep } from "./types";

const steps = (demoHints as { steps: DemoHintStep[] }).steps;

const STEP_BY_ID = new Map(steps.map((s) => [s.id, s]));

/** Steps that need a prior click/expand; collapse after F5. */
const GATE_FALLBACK: Readonly<Record<string, string>> = {
  "long-recommendation": "expand-profile",
  "long-profile": "expand-profile",
  "crm-product-groups": "expand-profile",
  "meeting-results-body": "meeting-results",
  "meeting-timecodes": "meeting-breakdown",
  "dashboard-metric-chart": "dashboard-first-employee",
  "dashboard-criteria-list": "dashboard-first-employee",
  "dashboard-details": "dashboard-first-employee",
};

const DASHBOARD_PANEL_STEPS = new Set([
  "dashboard-metric-chart",
  "dashboard-criteria-list",
  "dashboard-details",
]);

/** First hint on the designer detail page after «Подробнее». */
export const FIRST_DESIGNER_DETAIL_STEP_ID = "designer-overview";

/** Detail-page steps that need the first meeting expanded. */
const DESIGNER_MEETING_OPEN_STEPS = new Set([
  "designer-meeting-flags",
  "designer-meeting-summary",
  "designer-audio",
  "designer-criterion",
  "designer-timecode",
  "designer-issues",
  "designer-improve",
]);

/** Meeting overlay steps that die without live playback. */
const MEETING_LIVE_STEPS = new Set([
  "copilot-first-recommendation",
  "checkpoints",
  "visit-card",
  "missed-checkpoint",
]);

/** First hint shown on the manager dashboard after the sidebar button click. */
export const FIRST_DASHBOARD_PAGE_STEP_ID = "dashboard-overview";

export type TourWorld = {
  hasSelectedProfile: boolean;
  hasProfileContent: boolean;
  hasMeetingResults: boolean;
  isPlaybackActive: boolean;
  /** Long-form profile sections visible (expand survived). */
  isProfileExpanded?: boolean;
  /** Meeting results body visible. */
  isMeetingResultsOpen?: boolean;
  /** Meeting timecodes / breakdown visible. */
  isMeetingBreakdownOpen?: boolean;
  /** True while the manager dashboard route is mounted. */
  isOnDashboard?: boolean;
  /** Designer side panel is open on the dashboard. */
  isDesignerPanelOpen?: boolean;
  /** True while the designer detail page route is mounted. */
  isOnDesignerDetail?: boolean;
  /** First meeting card on the detail page is expanded. */
  isFirstMeetingOpen?: boolean;
  /** First criterion has a seekable timecode target. */
  hasCriterionTimecode?: boolean;
};

function knownStepId(stepId: string): string | null {
  return STEP_BY_ID.has(stepId) ? stepId : null;
}

function fallbackCreateProfile(world: TourWorld): string {
  if (world.hasSelectedProfile) return "processing-status";
  return "create-profile-open";
}

/**
 * Map a saved step to the nearest live checkpoint given current UI world.
 * Does not reconstruct meeting playback or processing hold.
 */
export function snapTourStep(stepId: string, world: TourWorld): string {
  const known = knownStepId(stepId);
  if (!known) return "create-profile-open";

  const step = STEP_BY_ID.get(known)!;
  const block = step.block ?? "";

  if (world.isOnDesignerDetail) {
    if (block === "dashboard-detail") {
      if (DESIGNER_MEETING_OPEN_STEPS.has(known) && !world.isFirstMeetingOpen) {
        return "designer-first-meeting";
      }
      if (known === "designer-timecode" && !world.hasCriterionTimecode) {
        return "designer-criterion";
      }
      return known;
    }
    // Dashboard list steps (or anything else) → start of detail tour.
    return FIRST_DESIGNER_DETAIL_STEP_ID;
  }

  if (world.isOnDashboard) {
    if (block === "dashboard-detail") {
      return "dashboard-details";
    }
    if (block === "dashboard") {
      if (DASHBOARD_PANEL_STEPS.has(known) && !world.isDesignerPanelOpen) {
        return GATE_FALLBACK[known] ?? "dashboard-first-employee";
      }
      return known;
    }
    return FIRST_DASHBOARD_PAGE_STEP_ID;
  }

  if (block === "dashboard" || block === "dashboard-detail") {
    return "dashboard";
  }

  // Gate steps: snap to opener unless the expanded UI is still present.
  const gate = GATE_FALLBACK[known];
  if (gate) {
    if (known === "long-recommendation" || known === "long-profile" || known === "crm-product-groups") {
      if (!world.isProfileExpanded) {
        return snapTourStep(gate, world);
      }
    }
    if (known === "meeting-results-body" && !world.isMeetingResultsOpen) {
      return snapTourStep(gate, world);
    }
    if (known === "meeting-timecodes" && !world.isMeetingBreakdownOpen) {
      return snapTourStep(gate, world);
    }
  }

  if (block === "meeting-results") {
    if (!world.hasMeetingResults) {
      if (world.hasProfileContent) return "start-recording";
      return fallbackCreateProfile(world);
    }
    return known;
  }

  if (block === "meeting") {
    if (known === "start-recording") {
      if (!world.hasProfileContent) return fallbackCreateProfile(world);
      return known;
    }
    // Live meeting UI (copilot / visit card / missed) needs playback.
    if (MEETING_LIVE_STEPS.has(known) && !world.isPlaybackActive) {
      if (world.hasMeetingResults) return "meeting-results";
      if (world.hasProfileContent) return "start-recording";
      return fallbackCreateProfile(world);
    }
    if (!world.hasProfileContent) return fallbackCreateProfile(world);
    return known;
  }

  if (block === "profile-content") {
    if (!world.hasProfileContent) return fallbackCreateProfile(world);
    return known;
  }

  if (block === "create-profile") {
    if (known === "create-profile-open") return known;
    if (!world.hasSelectedProfile) return "create-profile-open";
    // Early create steps still need a draft profile; later chrome steps are fine.
    return known;
  }

  return known;
}

/** Resolve step id to index in demo_hints; unknown → 0. */
export function stepIndexForId(stepId: string): number {
  const idx = steps.findIndex((s) => s.id === stepId);
  return idx >= 0 ? idx : 0;
}

export function isKnownTourStepId(stepId: string): boolean {
  return STEP_BY_ID.has(stepId);
}
